// engine/platform (Step 6, BASELINE_ARCHITECTURE §"Platform adapter") — the generic PlatformAdapter interface +
// the three adapters: poki (the shipped seam — its method set matches the real Poki SDK exactly; the SDK loads
// via a CDN <script>, NEVER an npm import, so the global is read defensively and every call is a safe no-op
// until M6 wires the real script), web (self-host/itch: no ads, no token), noop (headless/tests).
// The game picks one in config; UI/capabilities call the interface, never `PokiSDK.*`. The ad-lifecycle
// bracketing (panel open/close → gameplayStop → commercialBreak → gameplayStart) is a GAME-SUPPLIED policy hook
// riding this seam (the game's: the game) — a no-ad platform no-ops it for free.
//
// FAILURE ISOLATION (6j, seam-review C2): "every call is a safe no-op" must hold for a PRESENT-BUT-FAULTY SDK
// too, not just an absent one — a rejecting PokiSDK.init() (the classic adblock case Poki's docs say to continue
// past) would otherwise abort boot (main.ts awaits init() before app.start()), and a throwing lifecycle member
// would propagate through the ad-bracket into panel open/close. Every SDK member invocation is therefore
// isolated: sync throws are swallowed, async rejections resolve to the documented fallback.

export interface PlatformAdapter {
  /** True when no real SDK is present — every lifecycle call below is a safe no-op. */
  readonly isStub: boolean;
  init(): Promise<void>;
  gameLoadingFinished(): void;
  /** Call on FIRST input, not on load (Poki lifecycle, arch §1.4). */
  gameplayStart(): void;
  /** Call on pause / menu open / death. */
  gameplayStop(): void;
  /** Bracket a paused interval: gameplayStop → await commercialBreak → gameplayStart. */
  commercialBreak(): Promise<void>;
  /** Returns false on skip → caller grants no reward, raises no error (Poki rule). */
  rewardedBreak(): Promise<boolean>;
  /** Platform auth token (Poki: a 1-minute JWT verified server-side at M6); null when unavailable. */
  getToken(): Promise<string | null>;
  /** A URL parameter as the platform sees it (Poki sandboxes the page URL; web/dev reads location). */
  getURLParam(name: string): string | null;
}

interface PokiSDKLike {
  init?: () => Promise<void>;
  gameLoadingFinished?: () => void;
  gameplayStart?: () => void;
  gameplayStop?: () => void;
  commercialBreak?: () => Promise<void>;
  rewardedBreak?: () => Promise<boolean>;
  getToken?: () => Promise<string>;
  getURLParam?: (name: string) => string;
}

function sdk(): PokiSDKLike | undefined {
  return (globalThis as { PokiSDK?: PokiSDKLike }).PokiSDK;
}

/** Isolate a SYNC SDK call: a throwing member degrades to the no-op (the adapter's contract). */
function safe(fn: () => void): void {
  try {
    fn();
  } catch {
    /* a faulty SDK member must never reach game code */
  }
}

/** Isolate an ASYNC SDK call: a throw OR rejection degrades to the fallback value. */
async function safeAsync<T>(fn: () => Promise<T> | T | undefined, fallback: T): Promise<T> {
  try {
    return (await fn()) ?? fallback;
  } catch {
    return fallback;
  }
}

function locationParam(name: string): string | null {
  try {
    if (typeof location === 'undefined') return null;
    return new URL(location.href).searchParams.get(name);
  } catch {
    return null;
  }
}

/** The Poki seam (lifted verbatim from a client adapter — arch §1.4). Local-first: the SDK
 *  <script> is absent in dev, so every call is a safe no-op (`isStub === true`); the REAL SDK is wired at M6
 *  behind this same interface — zero game-code changes. No `@poki/sdk` import (the global is read defensively). */
export const pokiAdapter: PlatformAdapter = {
  get isStub(): boolean {
    return sdk() === undefined;
  },

  /** A rejecting/throwing SDK init (adblock) resolves anyway — boot must continue (main.ts awaits this). */
  async init(): Promise<void> {
    await safeAsync(() => sdk()?.init?.(), undefined);
  },

  gameLoadingFinished(): void {
    safe(() => sdk()?.gameLoadingFinished?.());
  },

  gameplayStart(): void {
    safe(() => sdk()?.gameplayStart?.());
  },

  gameplayStop(): void {
    safe(() => sdk()?.gameplayStop?.());
  },

  async commercialBreak(): Promise<void> {
    await safeAsync(() => sdk()?.commercialBreak?.(), undefined);
  },

  /** Dev stub / skip / SDK failure → false (no reward, no error). */
  async rewardedBreak(): Promise<boolean> {
    return safeAsync(() => sdk()?.rewardedBreak?.(), false);
  },

  /** null in local-first dev (the server stamps a dev identity) and on any SDK failure. */
  async getToken(): Promise<string | null> {
    return safeAsync<string | null>(() => sdk()?.getToken?.(), null);
  },

  getURLParam(name: string): string | null {
    try {
      const fromSdk = sdk()?.getURLParam?.(name);
      if (fromSdk !== undefined && fromSdk !== null) return fromSdk;
    } catch {
      /* fall through to location */
    }
    return locationParam(name);
  },
};

/** Self-host / itch / LAN web build: no ad platform — lifecycle no-ops, no rewards, no token; real URL params. */
export const webAdapter: PlatformAdapter = {
  isStub: true,
  init: () => Promise.resolve(),
  gameLoadingFinished: () => undefined,
  gameplayStart: () => undefined,
  gameplayStop: () => undefined,
  commercialBreak: () => Promise.resolve(),
  rewardedBreak: () => Promise.resolve(false),
  getToken: () => Promise.resolve(null),
  getURLParam: (name) => locationParam(name),
};

/** Headless / tests: everything inert, even URL params. */
export const noopAdapter: PlatformAdapter = {
  isStub: true,
  init: () => Promise.resolve(),
  gameLoadingFinished: () => undefined,
  gameplayStart: () => undefined,
  gameplayStop: () => undefined,
  commercialBreak: () => Promise.resolve(),
  rewardedBreak: () => Promise.resolve(false),
  getToken: () => Promise.resolve(null),
  getURLParam: () => null,
};
